import { apiServer } from "../config/config";

import {
  AccessibilityOption,
  AddPhotoImageData,
  Venue,
  VenueDetails,
} from "../types";
import {
  BasicTaggingInformationData,
  ComplexCategoriesData,
  ComplexConnectedSpacesOption,
  ComplexFloorTypeOption,
  ComplexLevelVisibilityOption,
  ComplexSpacePhotosResponse,
  ComplexVenueCategoriesResponse,
  LoadBasicTaggingCategoryResponse,
  LoadComplexCategorySpacesResponse,
  QuestionOption,
  TagQuestion,
} from "../types/tagging";
import User from "../types/user";
import { fetchWithToken } from "./fetchWithToken";

interface TagQuestionAnswer {
  level: number;
  categoryid: string;
  questionId: string;
  selectedAnswerId: string[];
}

export async function loadTagsBasicCategory(
  reviewId: string | null,
  venueId: string,
  categoryId: string,
  categoryLevel: string,
  typeQuestionTagId: string,
  token: string,
): Promise<LoadBasicTaggingCategoryResponse> {
  const formData = new FormData();
  formData.append("review_id", reviewId || "");
  formData.append("business_id", venueId);
  formData.append("category_id", categoryId);
  formData.append("category_level", categoryLevel);
  formData.append("typeQuestionTagId", typeQuestionTagId);
  formData.append("businessTemplateType", "Basic");
  console.log(formData);
  const response = await fetch(
    `${apiServer + "/category/loadtagsforbasiccategory"}`,
    {
      method: "POST",
      headers: {
        Authorization: token.length > 0 ? `Bearer ${token}` : "",
      },
      body: formData,
    },
  );
  const data = await response.json();
  if (data?.status === "error") {
    console.log(data);
    throw Error(data.message);
  } else {
    return data.data;
  }
}

export async function loadTaggingData(
  reviewId: string | null,
  venueId: string,
  categoryId: string,
  categoryLevel: string,
  typeQuestionTagId: string,
  token: string,
) {
  const taggingInfo = await loadTagsBasicCategory(
    reviewId,
    venueId,
    categoryId,
    categoryLevel,
    typeQuestionTagId,
    token,
  );
  const sections =
    taggingInfo?.filterOptions?.[0]?.levelOptions?.[0]?.questionSections ?? [];
  //find each sections questions
  const sectionQuestions = sections.map((section) => {
    const questions =
      taggingInfo?.filterOptions?.[0]?.levelOptions?.[0]?.questionOptions ?? [];
    const matchingQuestions = questions.filter(
      (question) =>
        question.category_section_id === section.category_section_id,
    );
    return matchingQuestions;
  });

  const answers: Map<number, TagQuestionAnswer> = new Map();
  for (const key in taggingInfo?.reviewTagsData) {
    const parts = key.split("_");
    const level = parts[0];
    const categoryid = parts[1];
    const questionId = parts[2];
    const selectedAnswerId = parts[3];
    const existing = answers.get(parseInt(questionId, 10)) || {
      level,
      categoryid,
      questionId,
      selectedAnswerId: [] as string[],
    };
    existing.selectedAnswerId.push(selectedAnswerId);
    answers.set(parseInt(questionId, 10), existing as TagQuestionAnswer);
  }
  console.log("loaded basic taggign data");
  return { taggingInfo, sectionQuestions, answers };
}

export interface LoadAllCategoriesResponseData {
  accessibilityOptions: AccessibilityOption[];
  gettingStarted: boolean;
  nextCategory: number;
  nextCategoryLevel: string;
  nextCategoryTypeQuestionId: number;
  taggingInfomration: any;
}

export interface LoadAllCategoriesResponse {
  status: string;
  message: string;
  data: LoadAllCategoriesResponseData;
}

export async function loadAllCategories(
  venueId: string,
  token: string,
): Promise<LoadAllCategoriesResponse> {
  const formData = new FormData();
  formData.append("category_level", "all");
  formData.append("businessId", venueId);
  formData.append("businessTemplateType", "Basic");
  formData.append("new_structure", "1");
  console.log("loading all categories");
  const response = await fetchWithToken(
    `${apiServer + "/category/loadallcategories"}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: token.length > 0 ? `Bearer ${token}` : "",
      },
      body: formData,
    },
  );
  const data = await response?.json();

  if (data?.status === "error") {
    console.log(data);
    throw new Error(data.data.message);
  } else {
    return data;
  }
}

export async function setNoBathroom(
  venueDetails: VenueDetails,
  optionTitleId: string,
  category: AccessibilityOption,
  accessToken: string,
) {
  const fd = new FormData();
  fd.append("tagId", optionTitleId);
  const response = await fetchWithToken(
    `${apiServer + "/category/loadtypequestionoptiondetail"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: fd,
    },
  );
  const data = await response?.json();
  if (data["status"] !== "success") {
    throw Error(data["message"]);
  }

  const levelCounter = data["data"]["levelCounter"];

  const formData = new FormData();
  formData.append("business_id", venueDetails.business.id.toString());
  formData.append("typeQuestionTagId", optionTitleId);
  formData.append("iterate_type", "all");
  formData.append("levelCounter", levelCounter.toString());
  formData.append("notes", "");
  formData.append(
    data["data"]["selectedTagName"],
    data["data"]["selectedTagValue"],
  );
  formData.append("parent_category_id", data["data"]["parent_category_id"]);
  formData.append(
    "parent_category_level",
    data["data"]["parent_category_level"],
  );
  const result = await fetchWithToken(
    `${apiServer + "/review/store-accessibility-multiple-photo"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return result?.json();
}

export async function deleteReview(reviewId: string, accessToken: string) {
  const formData = new FormData();
  formData.append("review_id", reviewId);
  const response = await fetchWithToken(
    `${apiServer + "/review/delete-review"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return response?.json();
}

export async function addBasicTaggingData(
  optionTitleId: string,
  venueDetails: VenueDetails,
  user: User,
  category: AccessibilityOption,
  optionAnswers: { [key: number]: string[] },
  taggingSections: QuestionOption[][],
  basicTaggingResponse: LoadBasicTaggingCategoryResponse,
  taggingData?: BasicTaggingInformationData,
  aiGeneratedTagIds?: string[],
) {
  const levelCounter = 1;
  const typeQuestionTagId = optionTitleId;
  const google_business_id =
    venueDetails.business.googleBusinessId ||
    (venueDetails.business as any).google_business_id;

  const formData = new FormData();

  formData.append(`business_id`, venueDetails.business.id.toString());
  formData.append("levelCounter", levelCounter.toString());
  formData.append("google_business_id", google_business_id);
  formData.append("typeQuestionTagId", typeQuestionTagId.toString());
  formData.append("parent_category_id", category.pageTitleId.toString());
  formData.append("parent_category_level", category.pageTitlecategoryLevel);
  formData.append(
    `${levelCounter}#${basicTaggingResponse.typeQuestionTagData.parent_category_id}_${basicTaggingResponse.typeQuestionTagData.category_id}`,
    `${levelCounter}#${basicTaggingResponse.typeQuestionTagData.parent_category_id}_${basicTaggingResponse.typeQuestionTagData.category_id}_${basicTaggingResponse.typeQuestionTagData.id}`,
  );
  console.log(
    `PKey: ${levelCounter}#${basicTaggingResponse.typeQuestionTagData.parent_category_id}_${basicTaggingResponse.typeQuestionTagData.category_id}`,
  );
  console.log(
    `PValue: ${levelCounter}#${basicTaggingResponse.typeQuestionTagData.parent_category_id}_${basicTaggingResponse.typeQuestionTagData.category_id}_${basicTaggingResponse.typeQuestionTagData.id}`,
  );
  for (const key in optionAnswers) {
    const value = optionAnswers[key];
    for (let i = 0; i < value.length; i++) {
      let type = "";
      taggingSections.forEach((section) => {
        section.forEach((question) => {
          if (question.titleId.toString() === key) {
            type = question.titleSelectionType;
          }
        });
      });
      if (type !== "Allow All") {
        console.log(
          `Key: ${type} - ${levelCounter}#${category.pageTitleId}_${key}`,
        );
        console.log(
          `Value: ${type} - ${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
        );
        formData.append(
          `${levelCounter}#${category.pageTitleId}_${key}`,
          `${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
        );
      } else {
        console.log(
          `Key: ${type} - ${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
        );
        console.log(
          `Value: ${type} - ${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
        );
        formData.append(
          `${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
          `${levelCounter}#${category.pageTitleId}_${key}_${value[i]}`,
        );
      }
    }
  }

  if (taggingData) {
    for (let index = 0; index < taggingData.images.length; index++) {
      const image = taggingData.images[index];

      formData.append(
        `${levelCounter}#levelImage||image_${index}`,
        {
          uri: image.imageUrl,
          type: "image/jpeg",
          name: `image_${index}.jpg`,
        } as any,
        `image_${index}.jpg`,
      );
      formData.append(
        `${levelCounter}#levelImage||alt_text_${index}`,
        image.note,
      );
      if (image.aiGeneratedAltText) {
        formData.append(
          `${levelCounter}#levelImage||ai_generated_alt_text_${index}`,
          image.aiGeneratedAltText,
        );
      }
    }
    formData.append(
      `${levelCounter}#levelImage`,
      taggingData.images.length.toString(),
    );
  }

  if (aiGeneratedTagIds && aiGeneratedTagIds.length > 0) {
    formData.append("ai_generated_tag_ids", aiGeneratedTagIds.join(", "));
  }

  console.log("formData", formData);
  const response = await fetchWithToken(
    `${apiServer + "/review/store-accessibility-multiple-photo"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${user?.accessToken}`,
      },
      body: formData,
    },
  );
  const data = await response?.json();
  return data;
}

export async function deleteMultipleReviewImages(
  businessId: string,
  images: string[],
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("review_and_review_photo_ids", JSON.stringify(images));
  const response = await fetchWithToken(
    `${apiServer + "/review/delete-multiple-review-photos"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return response?.json();
}

export async function deleteReviewImage(
  reviewId: string,
  reviewPhotoId: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("review_id", reviewId);
  formData.append("review_photo_id", reviewPhotoId);
  const response = await fetchWithToken(
    `${apiServer + "/review/delete-review-photo"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  return response?.json();
}

export async function updateImageDataForVenue(
  venueDetails: VenueDetails,
  user: User,
  category: AccessibilityOption,
  reviewIdData: any,
  taggingData?: BasicTaggingInformationData,
) {
  const levelCounter = 1;
  const google_business_id =
    venueDetails.business.googleBusinessId ||
    (venueDetails.business as any).google_business_id;
  console.log("google_business_id", google_business_id);
  //TODO:: double check this
  const formData = new FormData();

  console.log(`updating image data for venue ${venueDetails.business.id}`);
  formData.append(`business_id`, venueDetails.business.id.toString());
  formData.append("levelCounter", levelCounter.toString());
  formData.append("google_business_id", google_business_id);
  formData.append("parent_category_id", category.pageTitleId.toString());
  formData.append("parent_category_level", category.pageTitlecategoryLevel);

  const reviewId = reviewIdData[category.pageTitleId.toString()] || 0;
  formData.append("review_id", reviewId.toString());
  if (taggingData) {
    for (let index = 0; index < taggingData.images.length; index++) {
      const image = taggingData.images[index];

      if (image.noImage) {
        formData.append(
          `${levelCounter}#levelImage||image_${index}`,
          index.toString(),
        );
        formData.append(
          `${levelCounter}#levelImage||review_id_${index}`,
          (image.reviewId || 0).toString(),
        );
        formData.append(
          `${levelCounter}#levelImage||review_photo_id_${index}`,
          (image.reviewPhotosId || 0).toString(),
        );
      } else {
        const imageUri = image.imageUrl; // Assuming you want to send the first image
        const fileName = `image_${index}.jpg`; // Adjust accordingly

        const file = {
          uri: imageUri,
          type: "image/jpeg",
          name: fileName,
        };
        formData.append(
          `${levelCounter}#levelImage||image_${index}`,
          file as any,
          fileName,
        );
      }

      formData.append(
        `${levelCounter}#levelImage||alt_text_${index}`,
        image.note,
      );
    }
    formData.append(
      `${levelCounter}#levelImage`,
      taggingData.images.length.toString(),
    );
  }
  const response = await fetchWithToken(
    `${apiServer + "/review/edit-review-photos"}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${user?.accessToken}`,
      },
      body: formData,
    },
  );
  return await response?.json();
}

export async function uploadPhotos(
  venueId: string,
  imageData: AddPhotoImageData[],
  token: string,
) {
  const formData = new FormData();
  formData.append("business_id", venueId);
  formData.append("photoLength", imageData.length.toString());

  for (let i = 0; i < imageData.length; i++) {
    const imageUri = imageData[i].imageUrl;
    const fileName = `image${i}.jpg`;

    const file = {
      uri: imageUri,
      type: "image/jpeg",
      name: fileName,
    };
    formData.append(`image_${i}`, file as any, fileName);
    formData.append(`tag_applied_${i}`, imageData[i].selectedOption);
    formData.append(`alt_text_${i}`, imageData[i].note);
  }

  const response = await fetch(
    `${apiServer + "/review/store-multiple-photo-review"}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
        Authorization: token.length > 0 ? `Bearer ${token}` : "",
      },
      body: formData,
    },
  );
  const data = await response.json();
  if (data?.status === "error") {
    console.log(data);
    throw new Error(data.message);
  } else {
    return data.data;
  }
}

export async function loadComplexCategories(
  businessId: string,
  businessTemplateType: string,
  categoryLevel: string,
  newStructure: number,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("businessId", businessId);
  formData.append("businessTemplateType", businessTemplateType);
  formData.append("category_level", categoryLevel);
  formData.append("new_structure", newStructure.toString());
  const response = await fetchWithToken(
    `${apiServer}/category/loadcomplexcategories`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  const data = await response?.json();
  if (data?.status === "error") {
    throw new Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: ComplexVenueCategoriesResponse;
    };
  }
}

export async function loadComplexSubCategories(
  businessId: string,
  categoryId: number,
  templateType: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("businessId", businessId);
  formData.append("categoryId", categoryId.toString());
  formData.append("template_type", templateType);

  const response = await fetchWithToken(`${apiServer}/category/loadsubdata`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw new Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: ComplexCategoriesData[];
    };
  }
}

export async function loadComplexCategorySpacesData(
  businessId: string,
  categoryId: number,
  googleBusinessId: string,
  accessToken: string,
  reviewLevelId?: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("parent_category_id", categoryId.toString());

  if (reviewLevelId) {
    formData.append("review_level_id", reviewLevelId);
  }

  const response = await fetchWithToken(`${apiServer}/review/loadspaces`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw new Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        filterOptions: LoadComplexCategorySpacesResponse[];
        levelVisibilityOptions: ComplexLevelVisibilityOption[];
        floorTypeOptions: ComplexFloorTypeOption[];
        connectedSpacesOptions: ComplexConnectedSpacesOption[];
      };
    };
  }
}

export async function postCreateComplexSpace(
  businessId: string,
  googleBusinessId: string,
  parentCategoryId: number,
  notes: string,
  connectLevelId: number[],
  level: string,
  floors: string[],
  businessVisibilityId: string,
  accessToken: string,
  levelId?: number,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("parent_category_id", parentCategoryId.toString());
  formData.append("notes", notes);
  formData.append("connected_level_id", connectLevelId.join(","));
  if (levelId) {
    formData.append("levelId", levelId.toString());
  }
  formData.append("level", level);
  formData.append("floor", floors.join(","));
  formData.append("business_visibility_id", businessVisibilityId);

  const response = await fetchWithToken(`${apiServer}/review/store-space`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw new Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        review: string;
        review_id: number;
        review_level_id: number;
        coflicting_tags_count: number;
      };
    };
  }
}

export async function duplicateSpace(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
  parentCategoryId: string,
  levelNames: string[],
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);
  formData.append("parent_category_id", parentCategoryId);
  formData.append("levels", levelNames.join(","));

  const response = await fetchWithToken(`${apiServer}/review/duplicate-space`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: any;
    };
  }
}

export async function deleteSpace(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
  parentCategoryId: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);
  formData.append("parent_category_id", parentCategoryId);

  const response = await fetchWithToken(`${apiServer}/review/delete-space`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: any;
    };
  }
}

export async function loadTaggingQuestionsComplexSpace(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);

  const response = await fetchWithToken(
    `${apiServer}/review/loadtagsforspace`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        connectedSpacesOptions: ComplexConnectedSpacesOption[];
        filterOptions: LoadComplexCategorySpacesResponse[];
        floorTypeOptions: ComplexFloorTypeOption[];
        levelVisibilityOptions: ComplexLevelVisibilityOption[];
        reviewIdData: Map<string, string>;
        reviewTagsData: Map<string, string>;
      };
    };
  }
}

export async function setComplexSpaceTags(
  businessId: string,
  googleBusinessId: string,
  parentCategoryId: number,
  reviewId: string,
  reviewLevelId: string,
  questions: TagQuestion[],
  accessToken: string,
) {
  const levelCounter = 1;

  const formData = new FormData();

  formData.append(`business_id`, businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("parent_category_id", parentCategoryId.toString());
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);

  for (const question of questions) {
    if (question.question.titleSelectionType === "Allow All") {
      for (const answer of question.selectedAnswers) {
        formData.append(
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}`,
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}`,
        );
        console.log(
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}:${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}`,
        );
      }
    } else {
      for (const answer of question.selectedAnswers) {
        console.log(answer.optionTitle);
        formData.append(
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}`,
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}`,
        );
        console.log(
          `${levelCounter}#${parentCategoryId}_${question.question.titleId}:${levelCounter}#${parentCategoryId}_${question.question.titleId}_${answer.optionTitleId}`,
        );
      }
    }
  }

  console.log(formData);

  const response = await fetchWithToken(
    `${apiServer}/review/store-space-tags`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: any;
    };
  }
}

export async function loadComplexSpacePhotos(
  businessId: string,
  googleBusinessId: string,
  parentCategoryId: string,
  reviewId: string,
  reviewLevelId: string,
  sort_by: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("parent_category_id", parentCategoryId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);
  formData.append("sort_by", sort_by);

  const response = await fetchWithToken(
    `${apiServer}/review/load-space-photos`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: ComplexSpacePhotosResponse;
    };
  }
}

export async function uploadPhotosComplexSpace(
  businessId: string,
  googleBusinessId: string,
  parentCategoryId: string,
  reviewId: string,
  reviewLevelId: string,
  images: {
    url?: string;
    note: string;
    reviewPhotoId?: string;
    aiGeneratedAltText?: string;
  }[],
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);
  formData.append("parent_category_id", parentCategoryId);

  const levelCounter = 1;
  for (let index = 1; index < images.length + 1; index++) {
    const image = images[index - 1];
    const imageUri = image.url;

    if (!image.reviewPhotoId) {
      const fileName = `image_${index}.jpg`; // Adjust accordingly

      const file = {
        uri: imageUri,
        type: "image/jpeg",
        name: fileName,
      };
      formData.append(
        `${levelCounter}#levelImage||image_${index}`,
        file as any,
        fileName,
      );
    } else {
      formData.append(
        `${levelCounter}#levelImage||image_${index}`,
        index.toString(),
      );
      formData.append(
        `${levelCounter}#levelImage||review_id_${index}`,
        reviewId.toString(),
      );
      formData.append(
        `${levelCounter}#levelImage||review_photo_id_${index}`,
        image.reviewPhotoId?.toString() || "",
      );
    }
    formData.append(
      `${levelCounter}#levelImage||alt_text_${index}`,
      image.note,
    );
    if (image.aiGeneratedAltText) {
      formData.append(
        `${levelCounter}#levelImage||ai_generated_alt_text_${index}`,
        image.aiGeneratedAltText,
      );
    }
  }
  formData.append(`${levelCounter}#levelImage`, images.length.toString());

  console.log(formData);

  const response = await fetchWithToken(
    `${apiServer}/review/manage-space-photos`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        review: string;
        review_id: string;
        review_level_id: string;
        review_photo_id: string;
      };
    };
  }
}

export async function deleteComplexSpacePhotos(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
  review_photo_ids: string[],
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);
  formData.append("review_photo_ids", JSON.stringify(review_photo_ids));

  const response = await fetchWithToken(
    `${apiServer}/review/delete-space-photo-multiple`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );
  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: any;
    };
  }
}

export async function loadTaggingStatusComplexSpace(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);

  const response = await fetch(`${apiServer}/review/display-space-tags`, {
    method: "POST",
    body: formData,
  });

  const data = await response?.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        filterOptions: LoadComplexCategorySpacesResponse[];
      };
    };
  }
}

// Public API function for /review/display-space-tags (no authentication required)
export async function loadSpaceAccessibilityTagsPublic(
  businessId: string,
  googleBusinessId: string,
  reviewId: string,
  reviewLevelId: string,
) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  formData.append("google_business_id", googleBusinessId);
  formData.append("review_id", reviewId);
  formData.append("review_level_id", reviewLevelId);

  const response = await fetch(`${apiServer}/review/display-space-tags`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (data?.status === "error") {
    throw Error(data.message);
  } else {
    return data as {
      status: string;
      message: string;
      data: {
        filterOptions: LoadComplexCategorySpacesResponse[];
      };
    };
  }
}
